<script setup lang="ts">
import {
  PhBuildings as BuildingsIcon,
  PhCheckCircle as CheckCircleIcon,
  PhFactory as FactoryIcon,
  PhFileText as FileTextIcon,
  PhHandCoins as HandCoinsIcon,
  PhPhoneCall as PhoneCallIcon,
  PhShieldCheck as ShieldCheckIcon,
} from '@phosphor-icons/vue'
import { onMounted, ref } from 'vue'
import StatusState from '../components/StatusState.vue'
import { api } from '../services/api'
import type { TrustCenter } from '../types'

const trust = ref<TrustCenter | null>(null)
const loading = ref(true)
const error = ref('')
const isDev = import.meta.env.DEV

async function loadTrustCenter() {
  loading.value = true
  error.value = ''
  try {
    trust.value = await api.getTrustCenter()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '信任资料加载失败'
  } finally {
    loading.value = false
  }
}

onMounted(loadTrustCenter)
</script>

<template>
  <section class="page-hero page-hero--trust">
    <div class="container page-hero__inner">
      <div>
        <span class="eyebrow">服务保障</span>
        <h1>放心付款——每一笔钱都在明处</h1>
        <p>企业证照、收款账户、服务承诺，这里一次看清楚。</p>
      </div>
      <ShieldCheckIcon :size="88" weight="duotone" aria-hidden="true" />
    </div>
  </section>

  <section class="section">
    <div class="container">
      <StatusState v-if="loading" kind="loading" />
      <div v-else-if="error" class="identity-layout" aria-live="polite">
        <div class="identity-panel trust-fallback">
          <div class="identity-panel__heading">
            <BuildingsIcon :size="27" weight="duotone" aria-hidden="true" />
            <div>
              <h2>栖居木作</h2>
              <p>企业资料暂时无法载入</p>
            </div>
          </div>
          <div class="trust-fallback__content">
            <strong>如需联系请致电客服</strong>
            <p>客服号码与企业详细资料正在恢复，本页不会展示未经接口确认的联系方式。</p>
            <button class="button button--secondary" type="button" @click="loadTrustCenter">重新加载</button>
          </div>
        </div>
      </div>
      <template v-else-if="trust">
        <div class="identity-layout">
          <div class="identity-panel">
            <div class="identity-panel__heading">
              <BuildingsIcon :size="27" weight="duotone" aria-hidden="true" />
              <div>
                <h2>{{ trust.companyName }}</h2>
                <p>{{ trust.companyIdentifier }}</p>
              </div>
            </div>
            <dl class="identity-details">
              <div>
                <dt><BuildingsIcon :size="18" />办公地址</dt>
                <dd>{{ trust.officeAddress }}</dd>
              </div>
              <div>
                <dt><FactoryIcon :size="18" />合作工厂</dt>
                <dd>{{ trust.factoryAddress }}</dd>
              </div>
              <div>
                <dt><PhoneCallIcon :size="18" />客服与投诉</dt>
                <dd class="identity-details__phones">
                  <a :href="`tel:${trust.servicePhone}`">{{ trust.servicePhone }}</a>
                  <span aria-hidden="true">/</span>
                  <a :href="`tel:${trust.complaintPhone}`">{{ trust.complaintPhone }}</a>
                </dd>
              </div>
            </dl>
          </div>
          <aside class="payment-safety">
            <HandCoinsIcon :size="30" weight="duotone" aria-hidden="true" />
            <h2>付款安全提醒</h2>
            <p>正式订单只展示后台审核过的公司收款信息。请勿向销售或安装人员的个人账户支付大额款项。</p>
            <ul>
              <li>付款凭证上传后，由我们财务人员人工核验到账</li>
              <li>退款保留原收款和退款记录</li>
            </ul>
          </aside>
        </div>

        <div class="section-heading section-heading--spaced">
          <h2>我们对您的四项承诺</h2>
          <p>承诺内容与您下单时一致，不会事后修改。</p>
        </div>
        <div class="guarantee-grid">
          <article v-for="item in trust.guarantees" :key="item.title">
            <CheckCircleIcon :size="23" weight="fill" aria-hidden="true" />
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </article>
        </div>
      </template>
    </div>
  </section>

  <section class="section section--subtle">
    <div class="container confirmation-explainer">
      <div class="section-heading">
        <h2>开始生产前，我们会和您一起确认每一个细节</h2>
        <p>如有任何修改，需要重新确认，确保您看到的就是最终生产的版本。</p>
      </div>
      <div class="confirmation-quadrants">
        <article>
          <span>尺寸确认</span>
          <h3>量房报告与现场条件</h3>
          <p>确认成品尺寸、多点测量、障碍物、入户和安装条件。</p>
        </article>
        <article>
          <span>方案确认</span>
          <h3>图纸与开门方向</h3>
          <p>确认正视图、侧视图、内部结构、收口和图纸版本。</p>
        </article>
        <article>
          <span>材质五金</span>
          <h3>编号比名称更可靠</h3>
          <p>确认颜色编号、五金品牌、型号和整套方案说明。</p>
        </article>
        <article>
          <span>生产规则</span>
          <h3>冻结点与退款边界</h3>
          <p>确认交期、改动费用、生产冻结点和保障规则版本。</p>
        </article>
      </div>
      <div v-if="isDev" class="policy-note">
        <FileTextIcon :size="24" weight="duotone" aria-hidden="true" />
        <div>
          <strong>演示环境说明</strong>
          <p>当前证照、地址和合同为占位内容，正式上线前必须由业务负责人和法律顾问审核替换。</p>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.trust-fallback {
  grid-column: 1 / -1;
}

.trust-fallback__content {
  display: grid;
  justify-items: start;
  gap: 10px;
  padding-top: 26px;
}

.trust-fallback__content strong {
  color: var(--text);
  font-size: 1.08rem;
}

.trust-fallback__content p {
  max-width: 52ch;
  margin: 0 0 8px;
  color: var(--text-soft);
}
</style>
